const KEY = "bg_theme";
// v2: Brioni gold became the default accent (Jun 2026) — old "indigo" choices reset once.
const ACCENT_KEY = "crm_accent_v2";
const APPEARANCE_KEY = "crm_appearance";
const GOLD_KEY = "crm_gold";

// The staff app's yellow/gold highlights (AR balance, reminder buttons, active tabs, badges)
// follow this colour — set independently of the main accent via Appearance → Highlight colour.
export const DEFAULT_GOLD = "#c39e6d";

export const ACCENTS = [
  "gold",
  "indigo",
  "blue",
  "sky",
  "cyan",
  "teal",
  "emerald",
  "lime",
  "amber",
  "orange",
  "copper",
  "crimson",
  "rose",
  "pink",
  "fuchsia",
  "violet",
  "slate",
];

export const getTheme = () => localStorage.getItem(KEY) || "dark";

export const getAccent = () => {
  const a = localStorage.getItem(ACCENT_KEY) || "gold";
  return ACCENTS.includes(a) ? a : "gold";
};

export const applyTheme = (theme) => {
  document.body.classList.toggle("theme-light", theme === "light");
  localStorage.setItem(KEY, theme);
  // Regenerate the appearance layer so it respects the new theme immediately
  // (in light mode it must not stomp the light preset).
  applyAppearance(getAppearance());
};

// body.crm-accent-{key} recolors the admin brand accent.
export const applyAccent = (key) => {
  const a = ACCENTS.includes(key) ? key : "gold";
  ACCENTS.forEach((k) =>
    document.body.classList.toggle(`crm-accent-${k}`, k === a),
  );
  localStorage.setItem(ACCENT_KEY, a);
};

// ---- Highlight (gold) colour — recolours every amber/yellow highlight in the admin ----
export const getGold = () => localStorage.getItem(GOLD_KEY) || DEFAULT_GOLD;

const _rgb3 = (hex) => {
  const c = _hex(hex);
  return c ? `${c[0]} ${c[1]} ${c[2]}` : "217 180 106";
};
const _mix = (hex, target, amt) => {
  const c = _hex(hex);
  if (!c) return hex === target ? "217 180 106" : _rgb3(hex);
  return c.map((v) => Math.round(v + (target - v) * amt)).join(" ");
};
const _lum = (hex) => {
  const c = _hex(hex);
  if (!c) return 0.5;
  const [r, g, b] = c.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// Injects a single <style> tag setting the --crm-gold* vars on the .crm root; the
// class→var mappings live statically in crm-gold.css.
export const applyGold = (hex0) => {
  const hex = hex0 || DEFAULT_GOLD;
  const base = _rgb3(hex);
  const hover = _mix(hex, 0, 0.12); // 12% toward black
  const text = _mix(hex, 255, 0.42); // 42% toward white — readable on dark panels
  const fg = _lum(hex) > 0.45 ? "26 26 24" : "255 255 255";
  let el = document.getElementById("crm-gold");
  if (!el) {
    el = document.createElement("style");
    el.id = "crm-gold";
    document.head.appendChild(el);
  }
  el.textContent = `.crm[data-testid="crm-root"]{--crm-gold:${base};--crm-gold-hover:${hover};--crm-gold-text:${text};--crm-gold-fg:${fg};}`;
  localStorage.setItem(GOLD_KEY, hex);
};

export const resetGold = () => {
  localStorage.removeItem(GOLD_KEY);
  applyGold(DEFAULT_GOLD);
};

// Ready-made highlight swatches for the Appearance picker.
export const GOLD_PRESETS = [
  { key: "gold", label: "Brioni gold", hex: "#c39e6d" },
  { key: "amber", label: "Amber", hex: "#d99a2b" },
  { key: "honey", label: "Honey", hex: "#e0b552" },
  { key: "yellow", label: "Yellow", hex: "#eab308" },
  { key: "brass", label: "Brass", hex: "#b8873b" },
  { key: "champagne", label: "Champagne", hex: "#d8c48a" },
  { key: "bronze", label: "Bronze", hex: "#a5702e" },
];

// ---- Custom surface colors (main / sidebar / panel / font + panel transparency) ----
// Defaults match the shipped navy admin theme.
export const PRESETS = [
  { key: "navy", label: "Brioni navy", canvas: "#1e1e30", sidebar: "#1e1e30", panel: "#2a2a40", ink: "#f2f2fa" },
  { key: "graphite", label: "Graphite", canvas: "#141416", sidebar: "#111113", panel: "#1f1f23", ink: "#f4f4f5" },
  { key: "midnight", label: "Midnight blue", canvas: "#0b1220", sidebar: "#0a0f1a", panel: "#131c2e", ink: "#e6edf7" },
  { key: "forest", label: "Forest", canvas: "#0f1a16", sidebar: "#0c1512", panel: "#17261f", ink: "#e9f3ee" },
  { key: "espresso", label: "Espresso", canvas: "#1c1512", sidebar: "#17110e", panel: "#2a201b", ink: "#f5efe9" },
  { key: "plum", label: "Plum", canvas: "#1b1224", sidebar: "#160e1e", panel: "#281a35", ink: "#f3ecf8" },
  { key: "stone", label: "Warm stone", canvas: "#23211f", sidebar: "#1e1c1a", panel: "#2f2c29", ink: "#f5f2ee" },
  { key: "contrast", label: "High contrast", canvas: "#000000", sidebar: "#000000", panel: "#111111", ink: "#ffffff" },
];

export const DEFAULT_APPEARANCE = {
  canvas: "#1e1e30", // main page background  (rgb 30 30 48)
  sidebar: "#1e1e30", // side menu background
  panel: "#2a2a40", // cards / panels        (rgb 42 42 64)
  ink: "#f2f2fa", // primary font color   (rgb 242 242 250)
  muted: "#b4b4c8", // secondary / label text (dark mode readability)
  alpha: 1, // panel opacity 0.4 – 1
  font: "default", // admin typeface
};

export const FONTS = {
  default: '"PT Root UI", sans-serif',
  inter: '"Inter", sans-serif',
  manrope: '"Manrope", sans-serif',
  archivo: '"Archivo", sans-serif',
  system: 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
};

export const FONT_OPTIONS = [
  { key: "default", label: "PT Root UI", stack: FONTS.default },
  { key: "inter", label: "Inter", stack: FONTS.inter },
  { key: "manrope", label: "Manrope", stack: FONTS.manrope },
  { key: "archivo", label: "Archivo", stack: FONTS.archivo },
  { key: "system", label: "System", stack: FONTS.system },
  { key: "serif", label: "Serif", stack: FONTS.serif },
];

export const getAppearance = () => {
  try {
    const raw = localStorage.getItem(APPEARANCE_KEY);
    if (!raw) return { ...DEFAULT_APPEARANCE };
    return { ...DEFAULT_APPEARANCE, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_APPEARANCE };
  }
};

const _hex = (hex) => {
  const h = String(hex || "").replace("#", "");
  const n =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  const int = parseInt(n, 16);
  if (Number.isNaN(int) || n.length !== 6) return null;
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
};

const hexToRgb = (hex) => {
  const c = _hex(hex);
  return c ? `${c[0]} ${c[1]} ${c[2]}` : "0 0 0";
};

const hexToHsl = (hex) => {
  const c = _hex(hex);
  if (!c) return "0 0% 0%";
  let r = c[0] / 255,
    g = c[1] / 255,
    b = c[2] / 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b),
    d = max - min;
  let h = 0;
  const l = (max + min) / 2;
  const s = d === 0 ? 0 : l > 0.5 ? d / (2 - max - min) : d / (max + min);
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const buildAppearanceCss = (a) => {
  const al = Math.min(1, Math.max(0.4, Number(a.alpha) || 1));
  const glass = al < 0.96 ? "backdrop-filter: blur(7px);" : "";
  const fam = a.font && a.font !== "default" ? FONTS[a.font] || "" : "";
  // Scoped to the root's own testid so these always out-specify crm-theme.css
  // regardless of stylesheet source order.
  const S = '.crm[data-testid="crm-root"]';
  const fontRule = fam
    ? `${S}, ${S} :where(*):not(.font-mono-data):not([class*="font-mono"]) { font-family: ${fam} !important; }`
    : "";
  // Light theme = the Vuexy light preset (body.theme-light) owns every surface
  // and text color. The dark-oriented appearance surfaces would otherwise win
  // on specificity and leave dark panels / unreadable text. Keep only the font.
  if (getTheme() === "light") {
    return fontRule ? `\n${fontRule}\n` : "";
  }
  const canvas = hexToRgb(a.canvas);
  const sidebar = hexToRgb(a.sidebar);
  const panel = hexToRgb(a.panel);
  const ink = hexToRgb(a.ink);
  const muted = hexToRgb(a.muted || DEFAULT_APPEARANCE.muted);
  return `
${S} {
  --background: ${hexToHsl(a.canvas)};
  --card: ${hexToHsl(a.panel)};
  --popover: ${hexToHsl(a.panel)};
  --foreground: ${hexToHsl(a.ink)};
  --card-foreground: ${hexToHsl(a.ink)};
  --popover-foreground: ${hexToHsl(a.ink)};
  --muted-foreground: ${hexToHsl(a.muted || DEFAULT_APPEARANCE.muted)};
  background-color: rgb(${canvas}) !important;
  color: rgb(${ink}) !important;
}
/* Dark-mode readability: lift the dimmest zinc text tones to the chosen muted color */
${S} .text-zinc-600, ${S} .text-zinc-700 { color: rgb(${muted} / 0.75) !important; }
${S} .text-zinc-500 { color: rgb(${muted} / 0.9) !important; }
${S} .text-zinc-400 { color: rgb(${muted}) !important; }
${S} .placeholder\\:text-zinc-600::placeholder, ${S} input::placeholder, ${S} textarea::placeholder { color: rgb(${muted} / 0.7) !important; }
${S} .bg-zinc-950 { background-color: rgb(${canvas}) !important; }
${S} aside[data-testid="app-sidebar"],
${S} .app-shell-offset { background-color: rgb(${sidebar}) !important; }
${S} .bg-zinc-900,
${S} main .bg-zinc-950.border.rounded-lg,
${S} main .bg-zinc-950.border.rounded-xl {
  background-color: rgb(${panel} / ${al}) !important;
  ${glass}
}
${S} .text-zinc-100,
${S} .text-zinc-200,
${S} .text-zinc-300 { color: rgb(${ink}) !important; }
${fontRule}
`;
};

// Injects (or updates) a single <style> tag so custom colors win over crm-theme.css
// and apply regardless of when the .crm root mounts.
export const applyAppearance = (a0) => {
  const a = { ...DEFAULT_APPEARANCE, ...(a0 || {}) };
  let el = document.getElementById("crm-appearance");
  if (!el) {
    el = document.createElement("style");
    el.id = "crm-appearance";
    document.head.appendChild(el);
  }
  el.textContent = buildAppearanceCss(a);
  localStorage.setItem(APPEARANCE_KEY, JSON.stringify(a));
};

export const resetAppearance = () => {
  localStorage.removeItem(APPEARANCE_KEY);
  applyAppearance(DEFAULT_APPEARANCE);
};

const SHOP_KEY = "bg_shop_theme_at";
// Apply a shop-wide theme pushed by an admin (only once per push so staff can still tweak locally).
export const applyShopTheme = (shop) => {
  if (!shop || !shop.pushed_at || !shop.enforce) return false;
  if (localStorage.getItem(SHOP_KEY) === shop.pushed_at) return false;
  applyTheme(shop.theme || "dark");
  applyAccent(shop.accent || "gold");
  applyGold(shop.gold || DEFAULT_GOLD);
  applyAppearance({ ...DEFAULT_APPEARANCE, ...(shop.appearance || {}) });
  localStorage.setItem(SHOP_KEY, shop.pushed_at);
  return true;
};

// WCAG relative-luminance contrast ratio between two hex colors (1–21).
export const contrastRatio = (hexA, hexB) => {
  const lum = (hex) => {
    const [r, g, b] = hexToRgb(hex).split(" ").map((v) => Number(v) / 255);
    const f = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
  };
  const a = lum(hexA);
  const b = lum(hexB);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
};

applyTheme(getTheme());
applyAccent(getAccent());
applyGold(getGold());
applyAppearance(getAppearance());
